import type { AppointmentFilters } from '@/hooks/useAppointments';
import { getTodayCO } from '@/lib/dateUtils';
import { theme } from '@/lib/theme';

interface Props {
  filters: AppointmentFilters;
  onUpdate: (f: Partial<AppointmentFilters>) => void;
  onClear: () => void;
  total: number;
}

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'confirmed', label: 'Confirmada' },
  { value: 'pending', label: 'Esperando pago' },
  { value: 'no_show', label: 'No se presentó' },
  { value: 'cancelled', label: 'Cancelada' },
  { value: 'completed', label: 'Completada' },
];

const todayStr = getTodayCO();

function isToday(date: string) {
  return date === todayStr;
}

export default function AppointmentsFilters({ filters, onUpdate, onClear, total }: Props) {
  const hasActiveFilters = filters.date !== '' || filters.status !== '';
  const showingToday = isToday(filters.date);

  return (
    <div
      className="rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap border"
      style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
    >
      {/* Date filter */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
          <i className="ri-calendar-line text-base" style={{ color: theme.colors.textMuted }} />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={filters.date}
            onChange={(e) => onUpdate({ date: e.target.value })}
            className="text-white text-sm rounded-lg px-3 py-2 focus:outline-none transition-colors cursor-pointer border"
            style={{
              backgroundColor: 'rgba(255,255,255,0.04)',
              borderColor: theme.colors.border,
              colorScheme: 'dark',
            }}
          />
          {showingToday && filters.date !== '' && (
            <span
              className="text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap border"
              style={{
                color: theme.colors.primary,
                backgroundColor: theme.colors.primaryMuted,
                borderColor: theme.colors.primaryBorder,
              }}
            >
              Hoy
            </span>
          )}
        </div>
      </div>

      {/* Status filter */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
          <i className="ri-filter-3-line text-base" style={{ color: theme.colors.textDisabled }} />
        </div>
        <select
          value={filters.status}
          onChange={(e) => onUpdate({ status: e.target.value })}
          className="text-white text-sm rounded-lg px-3 py-2 focus:outline-none transition-colors cursor-pointer appearance-none pr-8 border"
          style={{
            backgroundColor: 'rgba(255,255,255,0.04)',
            borderColor: theme.colors.border,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23888888\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 10px center',
          }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value} className="bg-[#1C1C1C]">
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Show all button */}
      {filters.date !== '' && (
        <button
          onClick={() => onUpdate({ date: '' })}
          className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-all cursor-pointer whitespace-nowrap border"
          style={{
            backgroundColor: 'rgba(255,255,255,0.04)',
            borderColor: theme.colors.border,
            color: theme.colors.textSecondary,
          }}
        >
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-calendar-2-line text-xs" />
          </div>
          Ver todas las fechas
        </button>
      )}

      {/* Results count + clear all */}
      <div className="flex items-center gap-3 sm:ml-auto">
        <span className="text-xs whitespace-nowrap" style={{ color: theme.colors.textDisabled }}>
          {total} resultado{total !== 1 ? 's' : ''}
        </span>
        {hasActiveFilters && (
          <button
            onClick={onClear}
            className="flex items-center gap-1.5 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap hover:opacity-80"
            style={{ color: theme.colors.textSecondary }}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-close-line text-xs" />
            </div>
            Limpiar todo
          </button>
        )}
      </div>
    </div>
  );
}
